import { useEffect, useReducer, useState } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";

import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2104Context } from "../U2104Context";
import { NS, U2104ROLES } from "../U2104";
import { doCreateRoleData, doDeleteRoleData, doGetRoleAuditsData, doGetRoleIdData, doUpdateRoleData, emptyRole, luHandleAppiError, type U2104Role } from "../U2104Service";
import { U2104RoleFormToolBar } from "./form/u2104Role/U2104RoleFormToolBar";
import U2104RoleBody from "./form/u2104Role/U2104RoleBody";
import { useU2104UseGetOrganizatonRoleHandlers } from "./form/u2104Role/U2104UseGetOrganizatonRoleHandlers";
import U2104RoleActionsBody from "./form/u2104Role/U2104RoleActionTab/U2104RoleActionBody";
import U2104OrganizationRoleDeleteDlg from "./common/U2104OrganizationRoleDeleteDlg";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

// Interface à placer en dehors du composant pour éviter l'erreur de modificateur
export interface U2104RoleFormHandlers extends U2000FormStateHandlersReturn<U2104Role> {
  addNewRole: () => void;
  modifyRole: () => void;
  deleteRole: () => Promise<void>;
}

// Composant principal pour le formulaire de rôle
const U2104RoleForm: React.FC = () => {
  // Parameters
  const { id, roleId } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  // manage form initialization state
  const [hasInitialized, setHasInitialized] = useState(false);

  // Form state and reducer for u2101LogicalUnitBL
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  // manage active tab state
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // navigate et location
  const location = useLocation();
  const navigate = useNavigate();

  // Translation
  const { t } = useTranslation();
  const toast = useToast();

  // API Axios Client
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  // LU Handler,  Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridOrgRoleStateHandlers } = useU2104Context();

  // Form Handlers and default methods
  const validateForm = (role: U2104Role) => {
    const result: Record<string, string> = {};
    if (!role.roleCode) result.roleCode = t("errRoleCodeRequired", { ns: NS });
    if (!role.roleDescription) {
      result.roleDescription = t("errRoleDescriptionRequired", { ns: NS });
    }
    return result;
  };

  // Business Logic Handlers for U2104OrganizationRole
  const u2104OrganizationRoleBL: U2000FormSBusinessHandlers<U2104Role> = {
    newRecord: () => {
      return emptyRole;
    },

    // validation du formulaire
    validateForm: validateForm,

    // sauvegarde du formulaire
    saveForme: async (organizationRole: U2104Role, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(organizationRole);
      if (Object.keys(errors).length === 0) {
        handleSave.saveData(organizationRole, editMode);
        u2104RoleFormStateExtendedHandlers.setCustomAttributes({});
      } else {
        u2104RoleFormStateExtendedHandlers.setErrors(errors);
      }
    },

    // annulation du formulaire
    cancelForm: () => {
      const role = u2104RoleFormStateExtendedHandlers.getOldData() as U2104Role | null;
      if (role) {
        // Met à false checkedId pour chaque action
        if ("roles" in role && Array.isArray((role as any).roles)) {
          (role as any).roles = (role as any).roles.map((role: any) => ({ ...role, idChecked: false }));
        }
        u2104RoleFormStateExtendedHandlers.setData({ ...role });
      }
      u2104RoleFormStateExtendedHandlers.setCustomAttributes({});

      if (luStateHandlers.getActionOriginFrom() === "/U2104/ORGANIZATION") {
        navigate(`/U2104/${id}`, { replace: true });
      }
    },

    // gestion du changement d'enregistrement
    onChangeRecordForm: (organizationRole) => {
      if (organizationRole.id) {
        handleGet.getOrganizationRole(id!, organizationRole.id);
      }
    },

    // gestion de la sortie du formulaire
    onLeaveForm: () => {
      navigate(`/U2104/${id}`, { replace: true });
    },
  };

  // Handlers pour le formulaire d'un rôle
  const u2104RoleFormStateHandlers = useU2000FormStateHandlers<U2104Role>({
    formState,
    formStateDispatch,
    businessHandlers: u2104OrganizationRoleBL,
  });

  // extended methods
  const u2104RoleFormStateExtendedHandlers: U2104RoleFormHandlers = {
    ...u2104RoleFormStateHandlers,

    addNewRole: () => {
      u2104RoleFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    },
    modifyRole: () => {
      u2104RoleFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteRole: async () => {
      handleDelete.handleDelete();
    },
  };

  // Call Get Handlers for Logical Unit
  const handleGet: any = useU2104UseGetOrganizatonRoleHandlers<U2104Role & { id: string; roleId: string }>({
    doGetFn: (id: string, roleId: string) => {
      return doGetRoleIdData(u2000AxiosAuthenticated, id, roleId);
    },
    onSuccess: (role: U2104Role & { id: string; roleId: string }) => {
      gridOrgRoleStateHandlers.setDataById(role.id, role);
      u2104RoleFormStateExtendedHandlers.setData(role);

      // Remplace l'id dans l'URL par le nouvel id
      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(role.id));
      navigate(newPath, { replace: true });

      setLogicalUnitBreadcrumb([
        { href: "/U2104", label: t("title", { ns: NS }) },
        { href: `/U2104/${role.organization.id}`, label: role.organization.organizationName },
        { label: role.roleDescription + " (" + role.roleCode + ")" },
      ]);

      // Détermine le mode du formulaire selon l'action précédente
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u2104RoleFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Save Handlers for Logical Unit
  const handleSave = useU2000SaveHandlers<U2104Role>({
    doSaveFn: (role: U2104Role, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateRoleData(u2000AxiosAuthenticated, id!, role);
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateRoleData(u2000AxiosAuthenticated, id!, role.id, role);
      }
      // Ensure a Promise is always returned to satisfy the handler's expected signature
      return Promise.resolve(undefined);
    },
    onSuccess: (role: U2104Role & { id: string }, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridOrgRoleStateHandlers.setData([...gridOrgRoleStateHandlers.getData(), role]);
        gridOrgRoleStateHandlers.setTotalRows(gridOrgRoleStateHandlers.getTotalRows() + 1);

        // Remplace l'id dans l'URL par le nouvel id
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(role.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridOrgRoleStateHandlers.setDataById(role.id, role);
      }
      u2104RoleFormStateExtendedHandlers.afterSaveData(role);
      toast.success(t("msgRoleSaved", { ns: NS }));

      // Retourne à la liste après l'ajout ou la modification selon l'origine
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === "/U2104/ORGANIZATION") {
          navigate(`/U2104/${id}`, { replace: true });
        }
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);

      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u2104RoleFormStateExtendedHandlers.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u2104RoleFormStateExtendedHandlers.setErrors(errorResult.fieldErrors);
      }
    },
  });

  // Call Delete Handlers for Logical Unit
  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u2104RoleFormStateExtendedHandlers.getData()],
    doDeleteFn: async (roleId, row) => {
      return await doDeleteRoleData(u2000AxiosAuthenticated, id!, roleId, row);
    },

    onSuccess: (deletedRows) => {
      let newData = gridOrgRoleStateHandlers.getData();
      let currentIdx = gridOrgRoleStateHandlers.getCurrentIdx();
      let totalRows = gridOrgRoleStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: row["roleCode"], ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridOrgRoleStateHandlers.setData(newData);
      gridOrgRoleStateHandlers.setTotalRows(totalRows);

      // Apres suppression, aller au prochain record, aller vers l'avant si possible, sinon aller vers l'arrière, si aucun, retourner à la liste
      const action: U2000GridStateNextRecordAfterDeleteReturn = gridOrgRoleStateHandlers.getNextRecordAfterDeleteOnGridWithoutPagination(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getOrganizationRole(id!, action.id);
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate(`/U2104/${id}`, { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  // Wrapper adapté à U2000AuditPanelComponent: (axiosInstance, id) => Promise<...>
  const doGetRoleAuditsWrapper = (axiosInstance: any, roleId: string | number) => {
    return doGetRoleAuditsData(axiosInstance, id!, roleId!);
  };

  // Initialisation de lors du chargement de l'élément
  useEffect(() => {
    if (id && roleId && !hasInitialized) {
      if (roleId === "new") {
        u2104RoleFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
        u2104RoleFormStateExtendedHandlers.setData({ ...emptyRole });
      } else if (id && roleId) {
        handleGet.getOrganizationRole(id, roleId);
      } else {
        toast.error(t("errInvalidRoleId", { ns: NS }));
      }
    }
    setHasInitialized(true);
  }, [id, roleId, hasInitialized]);

  // Tabs du formulaire
  const formTabsComponent: any[] = [
    {
      id: "tabAction",
      label: t("tabActions", { ns: NS }),
      content: <U2104RoleActionsBody u2104RoleFormStateExtendedHandlers={u2104RoleFormStateExtendedHandlers} />,
    },
    hasRole(U2104ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: <U2000AuditPanelComponent id={u2104RoleFormStateExtendedHandlers.getData()?.id} doGetAuditsData={doGetRoleAuditsWrapper} toastName="U2104RoleAudit" />,
    },
  ].filter(Boolean);

  // Render
  return (
    <>
      <U2104OrganizationRoleDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000DefaultForm
        actionsBtns={<U2104RoleFormToolBar formStateHandlers={u2104RoleFormStateExtendedHandlers as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridOrgRoleStateHandlers}
        formStateHandlers={u2104RoleFormStateExtendedHandlers as any}
      >
        <U2104RoleBody formStateHandlers={u2104RoleFormStateExtendedHandlers as any} t={t} />

        <U2000Tab
          className="mt-4"
          tabs={formTabsComponent}
          defaultTab="tabAction"
          activeTab={activeTab ?? "tabAction"}
          setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))}
        />
      </U2000DefaultForm>
    </>
  );
};

export default U2104RoleForm;
